import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '@/context/ProjectContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft,
  Save,
  Settings,
  Target,
  BarChart3,
  MessageSquare,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Info,
  Bell,
  Clock,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Common timezones for selection
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central Europe (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
];

// Notification Settings Component
function NotificationSettings({ projectId }) {
  const [settings, setSettings] = useState({
    new_submission_mode: 'immediate',
    admin_timezone: 'America/New_York',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [sendingTest, setSendingTest] = useState(false);

  const fetchPendingCount = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/notification-settings/pending-count`);
      setPendingCount(res.data.pending_count || 0);
    } catch (err) {
      console.error('Failed to fetch pending count:', err);
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get(`/projects/${projectId}/notification-settings`);
        setSettings(res.data);
      } catch (err) {
        console.error('Failed to fetch notification settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
    fetchPendingCount();
  }, [projectId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/notification-settings`, settings);
      toast.success('Notification settings saved');
    } catch (err) {
      toast.error('Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestSummary = async () => {
    if (pendingCount === 0) {
      toast.info('No pending notifications to send');
      return;
    }
    setSendingTest(true);
    try {
      const res = await api.post(`/projects/${projectId}/notification-settings/test-daily-summary`);
      toast.success(`Daily summary sent! ${res.data.count} notification(s) included.`);
      fetchPendingCount(); // Refresh the count
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test summary');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="notification-settings-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#5AA2AE]" />
          Notification Settings
        </CardTitle>
        <CardDescription>
          Configure how you receive notifications about new stakeholder submissions
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Notification Mode */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">New Submission Notifications</Label>
          <div className="flex flex-col gap-3">
            <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
              <input
                type="radio"
                name="notification_mode"
                value="immediate"
                checked={settings.new_submission_mode === 'immediate'}
                onChange={() => setSettings(s => ({ ...s, new_submission_mode: 'immediate' }))}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-600" />
                  <span className="font-medium">Immediate</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Receive an email notification immediately when a new stakeholder submits feedback
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
              <input
                type="radio"
                name="notification_mode"
                value="daily_summary"
                checked={settings.new_submission_mode === 'daily_summary'}
                onChange={() => setSettings(s => ({ ...s, new_submission_mode: 'daily_summary' }))}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-600" />
                  <span className="font-medium">Daily Summary</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Receive a daily digest at 9 AM with all new submissions from the previous day
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Timezone Selection (only for daily summary) */}
        {settings.new_submission_mode === 'daily_summary' && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Your Timezone</Label>
            <Select 
              value={settings.admin_timezone} 
              onValueChange={(val) => setSettings(s => ({ ...s, admin_timezone: val }))}
            >
              <SelectTrigger className="w-full max-w-xs" data-testid="timezone-select">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map(tz => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              Daily summary emails will be sent at 9:00 AM in this timezone
            </p>
          </div>
        )}

        {/* Daily Summary Info */}
        {settings.new_submission_mode === 'daily_summary' && (
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Daily Summary includes:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• <strong>Self-Identified</strong>: Stakeholders who submitted via Public Landing</li>
                <li>• <strong>User Identified</strong>: Stakeholders added via About Others</li>
                <li>• Name, email, and proposed segments for each submission</li>
              </ul>
            </div>

            {/* Pending Notifications & Test Button */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-slate-900">Pending Notifications</h4>
                  <p className="text-sm text-slate-500">
                    {pendingCount === 0 
                      ? 'No pending notifications awaiting the next daily summary'
                      : `${pendingCount} notification${pendingCount !== 1 ? 's' : ''} queued for the next daily summary`
                    }
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendTestSummary}
                  disabled={sendingTest || pendingCount === 0}
                  data-testid="send-test-summary"
                  className="whitespace-nowrap"
                >
                  {sendingTest ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Now
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Click "Send Now" to immediately send the daily summary with all pending notifications
              </p>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end pt-2">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-[#5AA2AE] hover:bg-[#4a8a94]"
            data-testid="save-notification-settings"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Notification Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardConfigPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentProject, drivers } = useProject();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [config, setConfig] = useState({
    topline_question_id: null, // Single question for top-line score
    driver_summary_questions: {}, // { driver_id: [question_ids] }
    response_summary_questions: [], // Array of question IDs
    anonymity_threshold: 3
  });

  // Fetch questions and existing config
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [questionsRes, configRes] = await Promise.all([
        api.get(`/projects/${projectId}/questions`),
        api.get(`/projects/${projectId}/dashboard-config`).catch(() => ({ data: null }))
      ]);
      
      // Filter to only About Me questions
      const aboutMeQuestions = questionsRes.data.filter(q => 
        !q.question_context || q.question_context === 'about_me'
      );
      setQuestions(aboutMeQuestions);
      
      if (configRes.data) {
        setConfig(prev => ({
          ...prev,
          ...configRes.data
        }));
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle top-line question (only one allowed)
  const setToplineQuestion = (questionId) => {
    setConfig(prev => ({
      ...prev,
      topline_question_id: prev.topline_question_id === questionId ? null : questionId
    }));
  };

  // Toggle driver summary question
  const toggleDriverQuestion = (driverId, questionId) => {
    setConfig(prev => {
      const driverQuestions = prev.driver_summary_questions?.[driverId] || [];
      const newDriverQuestions = driverQuestions.includes(questionId)
        ? driverQuestions.filter(id => id !== questionId)
        : [...driverQuestions, questionId];
      
      return {
        ...prev,
        driver_summary_questions: {
          ...prev.driver_summary_questions,
          [driverId]: newDriverQuestions
        }
      };
    });
  };

  // Toggle response summary question
  const toggleResponseQuestion = (questionId) => {
    setConfig(prev => {
      const questions = prev.response_summary_questions || [];
      return {
        ...prev,
        response_summary_questions: questions.includes(questionId)
          ? questions.filter(id => id !== questionId)
          : [...questions, questionId]
      };
    });
  };

  // Save configuration
  const saveConfig = async () => {
    try {
      setSaving(true);
      await api.put(`/projects/${projectId}/dashboard-config`, config);
      toast.success('Configuration saved successfully');
      navigate(`/project/${projectId}/dashboards/summary`);
    } catch (err) {
      console.error('Failed to save config:', err);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Group questions by driver
  const questionsByDriver = {};
  questions.forEach(q => {
    const driverId = q.driver_id || 'unassigned';
    if (!questionsByDriver[driverId]) {
      questionsByDriver[driverId] = [];
    }
    questionsByDriver[driverId].push(q);
  });

  const getDriverName = (driverId) => {
    if (driverId === 'unassigned') return 'Unassigned';
    const driver = drivers?.find(d => d.id === driverId);
    return driver?.name || 'Unknown Driver';
  };

  // Check if question is numeric (can be used for top-line/driver summary)
  const isNumericQuestion = (q) => {
    return ['slider', 'scale', 'rating', 'numeric'].includes(q.question_type);
  };

  // Validation
  const hasToplineSelected = !!config.topline_question_id;
  const driversWithQuestions = Object.entries(config.driver_summary_questions || {})
    .filter(([_, questions]) => questions.length > 0);
  const hasResponseSummary = (config.response_summary_questions?.length || 0) > 0;

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate(`/project/${projectId}/dashboards/summary`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Dashboard Configuration
            </h1>
            <p className="text-sm text-slate-600">
              Configure which questions appear on the Project Summary
            </p>
          </div>
        </div>
        <Button 
          onClick={saveConfig}
          disabled={saving}
          className="gap-2"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Configuration
            </>
          )}
        </Button>
      </div>

      {/* Column Headers Legend */}
      <Card className="bg-slate-50">
        <CardContent className="p-0">
          <div className="grid grid-cols-12 gap-4 px-4 py-3 text-sm">
            <div className="col-span-4 flex items-center gap-2">
              <span className="font-medium">Driver / Question</span>
            </div>
            <div className="col-span-2 text-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="flex items-center justify-center gap-1 cursor-help">
                    <Target className="w-4 h-4 text-[#5AA2AE]" />
                    <span className="font-medium">Top-line Score</span>
                    <Info className="w-3 h-3 text-slate-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[250px]">
                    <p>Select ONE numeric question to display as the main sentiment score at the top of the dashboard.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="col-span-3 text-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="flex items-center justify-center gap-1 cursor-help">
                    <BarChart3 className="w-4 h-4 text-purple-500" />
                    <span className="font-medium">Driver Summary</span>
                    <Info className="w-3 h-3 text-slate-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[250px]">
                    <p>Select at least one numeric question per driver. Values are averaged to calculate each driver's score.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="col-span-3 text-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="flex items-center justify-center gap-1 cursor-help">
                    <MessageSquare className="w-4 h-4 text-emerald-500" />
                    <span className="font-medium">Response Summary</span>
                    <Info className="w-3 h-3 text-slate-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[250px]">
                    <p>Select questions to display as tiles in the Response Summary section.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Questions Table */}
      <Card>
        <CardContent className="p-0">
          <div className="divide-y">
            {Object.entries(questionsByDriver).map(([driverId, driverQuestions]) => (
              <div key={driverId}>
                {/* Driver Header */}
                <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-slate-100 items-center">
                  <div className="col-span-4">
                    <span className="font-semibold text-slate-700">{getDriverName(driverId)}</span>
                    <Badge variant="secondary" className="ml-2 text-xs">
                      {driverQuestions.length} questions
                    </Badge>
                  </div>
                  <div className="col-span-2 text-center text-xs text-slate-500">
                    {config.topline_question_id && driverQuestions.some(q => q.id === config.topline_question_id) ? (
                      <Badge className="bg-[#5AA2AE]">1 selected</Badge>
                    ) : '-'}
                  </div>
                  <div className="col-span-3 text-center text-xs text-slate-500">
                    {(config.driver_summary_questions?.[driverId]?.length || 0) > 0 ? (
                      <Badge className="bg-purple-500">{config.driver_summary_questions[driverId].length} selected</Badge>
                    ) : (
                      <span className="text-amber-600">Select at least 1</span>
                    )}
                  </div>
                  <div className="col-span-3 text-center text-xs text-slate-500">
                    {driverQuestions.filter(q => config.response_summary_questions?.includes(q.id)).length > 0 ? (
                      <Badge className="bg-emerald-500">
                        {driverQuestions.filter(q => config.response_summary_questions?.includes(q.id)).length} selected
                      </Badge>
                    ) : '-'}
                  </div>
                </div>

                {/* Questions */}
                {driverQuestions.map(q => {
                  const isNumeric = isNumericQuestion(q);
                  const isTopline = config.topline_question_id === q.id;
                  const isDriverSummary = config.driver_summary_questions?.[driverId]?.includes(q.id);
                  const isResponseSummary = config.response_summary_questions?.includes(q.id);

                  return (
                    <div 
                      key={q.id} 
                      className={`grid grid-cols-12 gap-4 px-4 py-3 items-center hover:bg-slate-50 ${isTopline ? 'bg-[#5AA2AE]/5' : ''}`}
                    >
                      {/* Question Name */}
                      <div className="col-span-4">
                        <p className="text-sm font-medium text-slate-700">{q.heading || q.text}</p>
                        <p className="text-xs text-slate-400">
                          {q.question_type} • {q.required ? 'Required' : 'Optional'}
                        </p>
                      </div>

                      {/* Top-line Score */}
                      <div className="col-span-2 flex justify-center">
                        {isNumeric ? (
                          <Switch
                            checked={isTopline}
                            onCheckedChange={() => setToplineQuestion(q.id)}
                            className={isTopline ? 'data-[state=checked]:bg-[#5AA2AE]' : ''}
                          />
                        ) : (
                          <span className="text-xs text-slate-300">N/A</span>
                        )}
                      </div>

                      {/* Driver Summary */}
                      <div className="col-span-3 flex justify-center">
                        {isNumeric ? (
                          <Switch
                            checked={isDriverSummary}
                            onCheckedChange={() => toggleDriverQuestion(driverId, q.id)}
                            className={isDriverSummary ? 'data-[state=checked]:bg-purple-500' : ''}
                          />
                        ) : (
                          <span className="text-xs text-slate-300">N/A</span>
                        )}
                      </div>

                      {/* Response Summary */}
                      <div className="col-span-3 flex justify-center">
                        <Switch
                          checked={isResponseSummary}
                          onCheckedChange={() => toggleResponseQuestion(q.id)}
                          className={isResponseSummary ? 'data-[state=checked]:bg-emerald-500' : ''}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Validation Summary */}
      <Card className={`${hasToplineSelected && driversWithQuestions.length > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            {hasToplineSelected && driversWithQuestions.length > 0 ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            )}
            <div className="space-y-1 text-sm">
              <p className={hasToplineSelected ? 'text-emerald-700' : 'text-amber-700'}>
                {hasToplineSelected ? '✓' : '○'} Top-line Score: {hasToplineSelected ? '1 question selected' : 'Select 1 numeric question'}
              </p>
              <p className={driversWithQuestions.length > 0 ? 'text-emerald-700' : 'text-amber-700'}>
                {driversWithQuestions.length > 0 ? '✓' : '○'} Driver Summary: {driversWithQuestions.length} driver(s) configured
              </p>
              <p className={hasResponseSummary ? 'text-emerald-700' : 'text-slate-500'}>
                {hasResponseSummary ? '✓' : '○'} Response Summary: {config.response_summary_questions?.length || 0} question(s) selected
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Anonymity Threshold */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Anonymity Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium">Minimum responses threshold:</label>
            <select
              value={config.anonymity_threshold || 3}
              onChange={(e) => setConfig(prev => ({ ...prev, anonymity_threshold: parseInt(e.target.value) }))}
              className="px-3 py-2 border rounded-md text-sm"
            >
              <option value={3}>3 responses</option>
              <option value={5}>5 responses</option>
              <option value={10}>10 responses</option>
            </select>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Data will be hidden if the number of responses is below this threshold.
          </p>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <NotificationSettings projectId={projectId} />
    </div>
  );
}
